import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import {
  FiRefreshCw,
  FiSearch,
  FiCalendar,
  FiDownload,
  FiCheckCircle,
  FiXCircle,
  FiAlertCircle,
  FiMapPin
} from "react-icons/fi";
import {
  MdOutlineGavel,
  MdOutlineCancel,
  MdOutlinePendingActions,
  MdOutlineCheckCircleOutline
} from "react-icons/md";
import "../styles/HearingManagement.css";

export default function HearingManagement() {
  const { token } = useAuth();

  const [hearings, setHearings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("ALL");
  const [exportLoading, setExportLoading] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    incomplete: 0,
    completed: 0,
    cancelled: 0
  });

  /* ======================
     FETCH HEARINGS
  ====================== */
  const fetchHearings = async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        tab: activeTab,
        ...(searchTerm && { search: searchTerm })
      }).toString();

      const res = await fetch(
        `${API_BASE_URL}/api/admin/hearings?${queryParams}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const data = await res.json();
      if (data.success) {
        setHearings(data.data || []);

        // Calculate stats from data
        const total = data.data?.length || 0;
        const incomplete = data.data?.filter(h => h.statusAtHearing === "INCOMPLETE").length || 0;
        const completed = data.data?.filter(h => h.statusAtHearing === "COMPLETED").length || 0;
        const cancelled = data.data?.filter(h => h.statusAtHearing === "CANCELLED").length || 0;

        setStats({
          total,
          incomplete,
          completed,
          cancelled
        });
      } else {
        toast.error("Failed to load hearings");
      }
    } catch (error) {
      toast.error("Failed to load hearings");
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHearings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  /* ======================
     EXPORT FUNCTIONALITY
  ====================== */
  const handleExport = async () => {
    try {
      setExportLoading(true);

      const params = new URLSearchParams({
        tab: activeTab,
        search: searchTerm,
        export: "excel"
      }).toString();

      const response = await fetch(
        `${API_BASE_URL}/api/admin/hearings/export?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (!response.ok) throw new Error("Export failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = `hearings_${Date.now()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success("Excel downloaded successfully");
    } catch (err) {
      toast.error("Excel export failed");
      console.error("Export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  /* ======================
     VIEW HEARING DETAILS
  ====================== */
  const handleViewHearingDetails = (hearing) => {
    Swal.fire({
      title: "Hearing Details",
      html: `
        <div style="text-align: left; line-height: 1.8; padding: 10px 0;">
          <div style="margin-bottom: 15px;">
            <strong style="color: #374151;">Case ID:</strong>
            <div style="color: #111827; font-family: monospace; background: #f3f4f6; padding: 8px; border-radius: 6px; margin-top: 4px;">
              ${hearing.caseId || "N/A"}
            </div>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 15px;">
            <div>
              <strong style="color: #374151;">User:</strong>
              <div style="color: #111827; margin-top: 4px;">${hearing.user?.name || "-"}</div>
            </div>
            <div>
              <strong style="color: #374151;">Solicitor:</strong>
              <div style="color: #111827; margin-top: 4px;">${hearing.solicitor?.name || "-"}</div>
            </div>
          </div>
          
          <div style="margin-bottom: 15px;">
            <strong style="color: #374151;">Court Details:</strong>
            <div style="color: #111827; margin-top: 4px;">
              ${hearing.courtDetails?.courtName || "N/A"}<br/>
              <small style="color: #6b7280;">${hearing.courtDetails?.address || ""}</small>
            </div>
          </div>
          
          <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 15px;">
            <div>
              <strong style="color: #374151;">Category:</strong>
              <div style="color: #111827; margin-top: 4px;">${hearing.practiceCategory || "-"}</div>
            </div>
            <div>
              <strong style="color: #374151;">Area:</strong>
              <div style="color: #111827; margin-top: 4px;">${hearing.practiceArea || "-"}</div>
            </div>
          </div>
        </div>
      `,
      icon: "info",
      confirmButtonText: "Close",
      confirmButtonColor: "#111827",
      width: "600px"
    });
  };

  /* ======================
     RENDER STATUS BADGE
  ====================== */
  const renderStatusBadge = (status) => {
    const statusConfig = {
      "INCOMPLETE": {
        color: "#f59e0b",
        icon: <MdOutlinePendingActions size={14} />,
        label: "Incomplete"
      },
      "COMPLETED": {
        color: "#10b981",
        icon: <MdOutlineCheckCircleOutline size={14} />,
        label: "Completed"
      },
      "CANCELLED": {
        color: "#dc2626",
        icon: <MdOutlineCancel size={14} />,
        label: "Cancelled"
      }
    };

    const config = statusConfig[status] || {
      color: "#6b7280",
      icon: <FiAlertCircle size={14} />,
      label: status || "Unknown"
    };

    return (
      <span
        className="hearing-management-status-badge"
        style={{
          backgroundColor: `${config.color}15`,
          color: config.color
        }}
      >
        {config.icon}
        <span>{config.label}</span>
      </span>
    );
  };

  /* ======================
     HEADER STATS COMPONENT
  ====================== */
  const renderHeaderStats = () => (
    <div className="hearing-management-stats-grid">
      <div className="hearing-management-stat-card hearing-management-stat-total">
        <div className="hearing-management-stat-icon">
          <MdOutlineGavel size={24} />
        </div>
        <div className="hearing-management-stat-content">
          <h3 className="hearing-management-stat-value">{stats.total}</h3>
          <p className="hearing-management-stat-label">Total Hearings</p>
        </div>
      </div>

      <div className="hearing-management-stat-card hearing-management-stat-incomplete">
        <div className="hearing-management-stat-icon">
          <MdOutlinePendingActions size={24} />
        </div>
        <div className="hearing-management-stat-content">
          <h3 className="hearing-management-stat-value">{stats.incomplete}</h3>
          <p className="hearing-management-stat-label">Incomplete</p>
          <span className="hearing-management-stat-subtext">Pending Hearing</span>
        </div>
      </div>

      <div className="hearing-management-stat-card hearing-management-stat-completed">
        <div className="hearing-management-stat-icon">
          <MdOutlineCheckCircleOutline size={24} />
        </div>
        <div className="hearing-management-stat-content">
          <h3 className="hearing-management-stat-value">{stats.completed}</h3>
          <p className="hearing-management-stat-label">Completed</p>
          <span className="hearing-management-stat-subtext">Hearing Done</span>
        </div>
      </div>

      <div className="hearing-management-stat-card hearing-management-stat-cancelled">
        <div className="hearing-management-stat-icon">
          <MdOutlineCancel size={24} />
        </div>
        <div className="hearing-management-stat-content">
          <h3 className="hearing-management-stat-value">{stats.cancelled}</h3>
          <p className="hearing-management-stat-label">Cancelled</p>
          <span className="hearing-management-stat-subtext">Not Held</span>
        </div>
      </div>
    </div>
  );

  return (
    <div className="premium-hearing-management">
      {/* ======================
         HEADER
      ====================== */}
      <div className="hearing-management-header">
        <div className="hearing-management-header-left">
          <h1 className="hearing-management-page-title">Hearing Management</h1>
          <p className="hearing-management-page-subtitle">
            Monitor and manage all court hearings
          </p>
        </div>
        <div className="hearing-management-header-right">
          <button
            className="hearing-management-refresh-btn"
            onClick={fetchHearings}
            disabled={loading}
          >
            <FiRefreshCw size={18} />
            <span>{loading ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button
            className="hearing-management-export-btn"
            onClick={handleExport}
            disabled={exportLoading || hearings.length === 0}
            title={hearings.length === 0 ? "No data to export" : "Export to Excel"}
          >
            {exportLoading ? (
              <>
                <div className="hearing-management-export-spinner"></div>
                <span>Exporting...</span>
              </>
            ) : (
              <>
                <FiDownload size={18} />
                <span style={{ color: "white" }}>Export ({hearings.length})</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ======================
         STATS
      ====================== */}
      {renderHeaderStats()}

      {/* ======================
         MAIN CONTENT
      ====================== */}
      <div className="hearing-management-content">
        {/* Controls */}
        <div className="hearing-management-controls">
          <div className="hearing-management-tabs-container">
            {["ALL", "INCOMPLETE", "COMPLETED", "CANCELLED"].map(tab => (
              <button
                key={tab}
                className={`hearing-management-tab-btn ${activeTab === tab ? "hearing-management-tab-active" : ""}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === "INCOMPLETE" && <MdOutlinePendingActions size={16} />}
                {tab === "COMPLETED" && <FiCheckCircle size={16} />}
                {tab === "CANCELLED" && <FiXCircle size={16} />}
                {tab === "ALL" && <FiCalendar size={16} />}
                <span>
                  {tab}
                  {tab === "ALL" && ` (${stats.total})`}
                  {tab === "INCOMPLETE" && ` (${stats.incomplete})`}
                  {tab === "COMPLETED" && ` (${stats.completed})`}
                  {tab === "CANCELLED" && ` (${stats.cancelled})`}
                </span>
              </button>
            ))}
          </div>

          <div className="hearing-management-controls-right">
            <div className="hearing-management-search-box">
              <FiSearch className="hearing-management-search-icon" />
              <input
                type="text"
                placeholder="Search by case, user, solicitor, status..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="hearing-management-search-input"
              />
            </div>
          </div>
        </div>

        {/* ======================
           TABLE
        ====================== */}
        <div className="hearing-management-table-container">
          {loading ? (
            <div className="hearing-management-loading-state">
              <div className="hearing-management-loading-spinner"></div>
              <p>Loading hearings...</p>
            </div>
          ) : (
            <table className="hearing-management-data-table">
              <thead>
                <tr>
                  <th>Case ID</th>
                  <th>User Information</th>
                  <th>Solicitor Information</th>
                  <th>Category & Area</th>
                  <th>Hearing Date & Time</th>
                  <th>Status</th>
                  <th>Court Details</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {hearings.length > 0 ? (
                  hearings.map(hearing => (
                    <tr key={hearing._id} className="hearing-management-data-row">
                      <td>
                        <div className="hearing-management-case-id">
                          <strong>{hearing.caseId || "N/A"}</strong>
                        </div>
                      </td>

                      <td>
                        <div className="hearing-management-user-info">
                          <div className="hearing-management-user-avatar">
                            {hearing.user?.userProfile?.photo ? (
                              <img
                                src={hearing.user.userProfile.photo}
                                alt={hearing.user?.name}
                                className="hearing-avatar-img"
                              />
                            ) : (
                              <div className="avatar-fallback">
                                {hearing.user?.name?.charAt(0)?.toUpperCase() || "U"}
                              </div>
                            )}
                          </div>

                          <div className="hearing-management-user-details">
                            <div className="hearing-management-user-name">
                              {hearing.user?.name || "-"}
                            </div>
                            <div className="hearing-management-user-meta">
                              <span className="hearing-management-user-email">
                                {hearing.user?.email || "-"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="hearing-management-solicitor-info">
                          <div className="hearing-management-solicitor-avatar">
                            {hearing.solicitor?.profile?.photo ? (
                              <img
                                src={hearing.solicitor.profile.photo}
                                alt={hearing.solicitor?.name}
                                className="hearing-avatar-img"
                              />
                            ) : (
                              <div className="avatar-fallback">
                                {hearing.solicitor?.name?.charAt(0)?.toUpperCase() || "S"}
                              </div>
                            )}
                          </div>

                          <div className="hearing-management-solicitor-details">
                            <div className="hearing-management-solicitor-name">
                              {hearing.solicitor?.name || "-"}
                            </div>
                            <div className="hearing-management-solicitor-meta">
                              <span className="hearing-management-solicitor-email">
                                {hearing.solicitor?.email || "No email"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="hearing-management-category-area">
                          <div className="hearing-management-category">
                            <span>{hearing.practiceCategory || "-"}</span>
                          </div>
                          <div className="hearing-management-area">
                            <small>{hearing.practiceArea || "-"}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="hearing-management-date-cell">
                          <div className="hearing-management-date">
                            {new Date(hearing.hearingDateTime).toLocaleDateString()}
                          </div>
                          <div className="hearing-management-time">
                            {new Date(hearing.hearingDateTime).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </td>

                      <td>
                        {renderStatusBadge(hearing.statusAtHearing)}
                      </td>

                      <td>
                        <div className="hearing-management-court-info">
                          <div className="hearing-management-court-name">
                            <FiMapPin size={12} />
                            <span>{hearing.courtDetails?.courtName || "-"}</span>
                          </div>
                          <div className="hearing-management-court-address">
                            <small>{hearing.courtDetails?.address || ""}</small>
                          </div>
                        </div>
                      </td>

                      <td>
                        <div className="hearing-management-action-buttons">
                          <button
                            className="hearing-management-view-btn"
                            onClick={() => handleViewHearingDetails(hearing)}
                            title="View Hearing Details"
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr className="hearing-management-no-data-row">
                    <td colSpan="8">
                      <div className="hearing-management-no-data">
                        <FiCalendar size={48} />
                        <p>No hearings found</p>
                        <small>Try adjusting your search or filters</small>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}